import { t } from '../i18n';
import { Link } from 'react-router-dom';
import { Code, Cpu, Zap, Lightbulb, ArrowRight } from 'lucide-react';
import { useEffect, useState } from 'react';

const About = () => {
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => setMounted(true), 100);
    return () => clearTimeout(timer);
  }, []);

  const skills = [
    { name: t("AI Technologies"), level: 100 },
    { name: 'C#', level: 80 },
    { name: 'React JS', level: 90 },
    { name: 'React Native', level: 85 },
    { name: 'Flutter', level: 80 },
    { name: t("Project Management"), level: 90 },
    { name: 'Azure', level: 85 },
    { name: 'GitHub/GitLab', level: 95 },
    { name: t("Process Automation"), level: 90 },
  ];

  const services = [
    {
      id: 'web-development',
      icon: Code,
      title: t("Web Development"),
      description: t("Building modern, responsive, and scalable web applications using the latest technologies."),
      technologies: ['React', 'Next.js', 'Vue.js', 'Node.js', 'Express', 'PostgreSQL']
    },
    {
      id: 'ai-integration',
      icon: Cpu,
      title: t("AI Integration"),
      description: t("Implementing artificial intelligence solutions to enhance your applications."),
      technologies: ['OpenAI', 'TensorFlow', 'PyTorch', 'Hugging Face', t("Computer Vision"), t("NLP")]
    },
    {
      id: 'automation',
      icon: Zap,
      title: t("Process Automation"),
      description: t("Streamlining workflows and automating repetitive tasks to improve efficiency."),
      technologies: ['RPA', t("Workflow Automation"), t("API Integration"), t("CI/CD Pipelines")]
    },
    {
      id: 'consulting',
      icon: Lightbulb,
      title: t("IT Consulting"),
      description: t("Expert advice on technology decisions, architecture, and digital strategy."),
      technologies: [t("Technology Strategy"), t("System Architecture"), t("Digital Transformation")]
    }
  ];

  const timeline = [
    { title: t("Independent - Founder of Virtuoz Solutions"), period: t("Feb 2020 - Present"), desc: t("Development of web and mobile solutions"), icon: Code },
    { title: t("IT Department Manager"), period: t("Oct 2020 - Present"), desc: "Prim'Holstein France, Angers", icon: Cpu },
    { title: t("Developer"), period: t("Oct 2019 - Oct 2020"), desc: "Prim'Holstein France, Angers", icon: Zap },
    { title: t("Research Engineer"), period: t("Sep 2017 - Oct 2019"), desc: 'Orkeis, Angers', icon: Lightbulb },
    { title: t("Web & Mobile Developer"), period: t("Sep 2015 - Sep 2017"), desc: 'Groupe MEDIS', icon: Code },
    { title: t("Programmer Analyst"), period: t("Sep 2014 - Aug 2015"), desc: t("Rapido, ERP development in .Net (C#)"), icon: Zap },
  ];

  return (
    <div className="content-panel">
      {/* Header */}
      <section
        className={`py-8 md:py-12 transition-all duration-700 ease-out-expo ${
          mounted ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
        }`}
      >
        {/* Decorative line */}
        <div className="w-12 h-px bg-gradient-to-r from-primary to-transparent mb-8" />

        <h1 className="font-display text-fluid-3xl mb-6">{t("About Me")}</h1>
        <p className="text-lg text-muted max-w-2xl leading-relaxed">
          {t("With over a decade of experience in development and IT, I'm passionate about creating")}{' '}
          <span className="text-foreground">{t("innovative solutions")}</span> {t("that leverage the latest technologies.")}
        </p>
      </section>

      {/* Bio Section */}
      <section className="py-8 md:py-12">
        <div className="grid grid-cols-1 gap-8 xl:grid-cols-[minmax(0,1.05fr)_minmax(360px,0.95fr)] 2xl:gap-12">
          {/* Bio Card */}
          <div
            className={`card p-6 transition-all duration-700 ease-out-expo ${
              mounted ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
            }`}
            style={{ transitionDelay: '100ms' }}
          >
            {/* Corner accent */}
            <div
              className="absolute top-0 right-0"
              style={{
                width: 0,
                height: 0,
                borderStyle: 'solid',
                borderWidth: '0 50px 50px 0',
                borderColor: 'transparent rgb(var(--color-primary) / 0.15) transparent transparent',
              }}
            />

            <h2 className="text-2xl font-heading font-bold mb-2">Enzo Chevallier</h2>
            <p className="text-primary font-heading text-sm uppercase tracking-widest mb-6">{t("Project Manager & Developer")}</p>
            <div className="space-y-4 text-muted leading-relaxed">
              <p>
                {t("I'm a passionate project manager and developer specializing in")}{' '}
                <span className="text-foreground">{t("C#, React JS, React Native, and Flutter")}</span>{t(". With extensive experience leading development teams, I excel at creating innovative solutions across various industries.")}
              </p>
              <p>
                {t("I leverage")} <span className="text-foreground">{t("Azure, GitHub, and GitLab")}</span> {t("in my workflow, with a particular focus on process automation to streamline development and operations.")}
              </p>
              <p>
                {t("I closely follow developments in AI models including")}{' '}
                <span className="text-foreground">{t("LLAMA, GPT, Claude, Gemini, and Deepseek")}</span>{t(", integrating the latest AI capabilities into my projects.")}
              </p>
            </div>
          </div>

          {/* Skills */}
          <div
            className={`transition-all duration-700 ease-out-expo ${
              mounted ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
            }`}
            style={{ transitionDelay: '200ms' }}
          >
            <h3 className="section-title">
              <span>{t("My Skills")}</span>
            </h3>
            <div className="space-y-5">
              {skills.map((skill, index) => (
                <div key={index}>
                  <div className="flex justify-between items-center mb-2">
                    <span className="text-sm font-heading font-medium text-foreground">{skill.name}</span>
                    <span className="text-xs text-muted">{skill.level}%</span>
                  </div>
                  <div className="skill-bar">
                    <div
                      className="skill-bar-fill"
                      style={{
                        width: mounted ? `${skill.level}%` : '0%',
                        transitionDelay: `${300 + index * 50}ms`,
                      }}
                    />
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section
        className={`py-8 md:py-12 transition-all duration-700 ease-out-expo ${
          mounted ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
        }`}
        style={{ transitionDelay: '300ms' }}
        id="services"
      >
        <h2 className="section-title">
          <span>Services</span>
        </h2>
        <div className="grid grid-cols-1 gap-6 md:grid-cols-2 2xl:grid-cols-4">
          {services.map((service, index) => {
            const Icon = service.icon;
            return (
              <div
                key={service.id}
                id={service.id}
                className="group card card-hover p-6 relative"
                style={{ transitionDelay: `${400 + index * 100}ms` }}
              >
                {/* Glow effect on hover */}
                <div className="absolute inset-0 rounded-xl opacity-0 group-hover:opacity-100 transition-opacity duration-500"
                  style={{
                    background: 'radial-gradient(circle at center, rgb(var(--color-primary) / 0.05) 0%, transparent 70%)',
                  }}
                />

                <div className="relative">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="p-2.5 bg-primary/10 rounded-lg group-hover:bg-primary/20 transition-colors duration-300">
                      <Icon className="w-5 h-5 text-primary" />
                    </div>
                    <h3 className="text-lg font-heading font-bold">{service.title}</h3>
                  </div>
                  <p className="text-muted text-sm mb-4 leading-relaxed">{service.description}</p>
                  <div className="flex flex-wrap gap-2">
                    {service.technologies.map((tech, techIndex) => (
                      <span
                        key={techIndex}
                        className="px-2 py-1 text-xs text-muted/80 bg-foreground/5 rounded font-heading tracking-wide"
                      >
                        {tech}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </section>

      {/* Timeline Section */}
      <section
        className={`py-8 md:py-12 transition-all duration-700 ease-out-expo ${
          mounted ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
        }`}
        style={{ transitionDelay: '400ms' }}
      >
        <h2 className="section-title">
          <span>{t("My Journey")}</span>
        </h2>
        <div className="space-y-8">
          {timeline.map((item, index) => {
            const Icon = item.icon;
            return (
              <div key={index} className="flex gap-4 group">
                <div className="flex flex-col items-center">
                  <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center group-hover:bg-primary/20 group-hover:shadow-glow transition-all duration-300">
                    <Icon className="w-5 h-5 text-primary" />
                  </div>
                  {index < timeline.length - 1 && (
                    <div className="w-px flex-1 bg-gradient-to-b from-primary/30 to-transparent mt-3" />
                  )}
                </div>
                <div className="pb-8">
                  <h3 className="font-heading font-bold text-foreground">{item.title}</h3>
                  <p className="text-primary text-sm font-heading tracking-wide mb-1">{item.period}</p>
                  <p className="text-muted text-sm">{item.desc}</p>
                </div>
              </div>
            );
          })}
        </div>
      </section>

      {/* CTA */}
      <section
        className={`py-8 md:py-12 transition-all duration-700 ease-out-expo ${
          mounted ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
        }`}
        style={{ transitionDelay: '500ms' }}
      >
        <div className="card p-8 text-center relative overflow-hidden">
          {/* Background gradient */}
          <div className="absolute inset-0 bg-gradient-to-br from-primary/5 via-transparent to-accent/5" />

          <div className="relative">
            <h2 className="text-2xl font-heading font-bold mb-4">{t("Let's Work Together")}</h2>
            <p className="text-muted mb-8 max-w-lg mx-auto">
              {t("Whether you have a specific project in mind or just want to explore possibilities, I'd love to hear from you.")}
            </p>
            <Link to="/contact" className="btn btn-primary">
              {t("Get in Touch")}
              <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default About;
